import { useState, useEffect, useCallback } from 'react';
import { CSS } from './constants/styles';
import { todayStr, todayDow } from './utils/date';
import { loadData, saveData, defaultData } from './utils/data';
import { getDayMeta, getDayExercises } from './utils/workout';
import { DEFAULT_PROGRAM } from './constants/defaultProgram';
import { requestNotifPermission, sendSwMessage } from './utils/notifications';
import WorkoutView from './components/WorkoutView';
import HistoryView from './components/HistoryView';
import StatsView from './components/StatsView';
import ToolsView from './components/ToolsView';
import SettingsView from './components/SettingsView';
import LoggingScreen from './components/LoggingScreen';
import ProgramEditorScreen from './components/ProgramEditorScreen';
import OnboardingScreen from './components/OnboardingScreen';
import RestTimer from './components/RestTimer';

const APP_VERSION = "1.0.24";

export default function App() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("workout");
  const [loggingEx, setLoggingEx] = useState(null);
  const [activeDow, setActiveDow] = useState(todayDow());
  const [showProgramEditor, setShowProgramEditor] = useState(false);

  const [timerEndTime, setTimerEndTime] = useState(null);
  const [timerTotalSecs, setTimerTotalSecs] = useState(0);

  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => { setData(loadData()); }, []);
  const persist = useCallback(nd => { setData(nd); saveData(nd); }, []);

  // Show onboarding for users with no sessions and no completed onboarding
  useEffect(() => {
    if (!data) return;
    if (!data.onboardingComplete && Object.keys(data.sessions).length === 0) {
      setShowOnboarding(true);
    }
  }, [data]);

  function handleOnboardingComplete({ dayStructure, customProgram, startingWeights }) {
    const newData = { ...data, dayStructure, customProgram, startingWeights, onboardingComplete: true };
    persist(newData);
    setShowOnboarding(false);
  }

  function handleStartTimer(secs) {
    requestNotifPermission();
    setTimerEndTime(Date.now() + secs * 1000);
    setTimerTotalSecs(secs);
    sendSwMessage({ type: 'SCHEDULE_TIMER', seconds: secs, label: 'Time for your next set!' });
  }
  function handleDismissTimer() {
    setTimerEndTime(null);
    sendSwMessage({ type: 'CANCEL_TIMER' });
  }

  if (!data) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100dvh", background: "#06090f" }}>
      <style>{CSS}</style>
      <span style={{ color: "#4a5978", fontSize: 14 }}>Loading...</span>
    </div>
  );

  if (showOnboarding) return <OnboardingScreen onComplete={handleOnboardingComplete} onCancel={Object.keys(data.sessions).length > 0 || data.onboardingComplete ? () => setShowOnboarding(false) : null} />;

  const today = todayStr();

  function ensureSession() {
    if (!data.sessions[today]) return { ...data, sessions: { ...data.sessions, [today]: { dayIndex: todayDow(), exercises: {}, notes: "", startTime: Date.now() } } };
    return data;
  }
  function handleSaveSets(exId, sets, _unused, exName) {
    let d = ensureSession();
    const se = { ...d.sessions[today] };
    const existing = se.exercises[exId] || {};
    // Preserve swappedFrom metadata if it exists — don't overwrite with old model
    se.exercises = { ...se.exercises, [exId]: { ...existing, sets, skipped: false, name: exName } };
    persist({ ...d, sessions: { ...d.sessions, [today]: se } });
  }

  function handleSwap(originalExId, originalExName, replacementEx) {
    let d = ensureSession();
    const se = { ...d.sessions[today] };
    se.exercises = {
      ...se.exercises,
      [originalExId]: {
        ...(se.exercises[originalExId] || {}),
        sets: [],
        swapped: true,
        replacedBy: replacementEx.id,
        replacedByName: replacementEx.name,
        skipped: false,
      },
      [replacementEx.id]: {
        ...(se.exercises[replacementEx.id] || {}),
        sets: se.exercises[replacementEx.id]?.sets || [],
        swappedFrom: originalExId,
        swappedFromName: originalExName,
        skipped: false,
      },
    };
    persist({ ...d, sessions: { ...d.sessions, [today]: se } });
  }

  function handleRestoreSwap(originalExId) {
    let d = ensureSession();
    const se = { ...d.sessions[today] };
    const original = se.exercises[originalExId];
    const replacementId = original?.replacedBy;
    const newExercises = { ...se.exercises };
    if (replacementId) delete newExercises[replacementId];
    newExercises[originalExId] = {
      ...newExercises[originalExId],
      sets: [],
      swapped: false,
      replacedBy: null,
      replacedByName: null,
    };
    se.exercises = newExercises;
    persist({ ...d, sessions: { ...d.sessions, [today]: se } });
  }
  function handleSkip(exId, currentlySkipped) {
    let d = ensureSession();
    const se = { ...d.sessions[today] };
    if (currentlySkipped) {
      se.exercises = { ...se.exercises, [exId]: { ...se.exercises[exId], skipped: false } };
    } else {
      se.exercises = { ...se.exercises, [exId]: { ...se.exercises[exId], skipped: true } };
    }
    persist({ ...d, sessions: { ...d.sessions, [today]: se } });
  }
  function handleSaveNotes(notes) {
    let d = ensureSession();
    persist({ ...d, sessions: { ...d.sessions, [today]: { ...d.sessions[today], notes } } });
  }
  function handleSaveCardio(cardioEntry) {
    let d = ensureSession();
    const existing = Array.isArray(d.sessions[today]?.cardio)
      ? d.sessions[today].cardio
      : d.sessions[today]?.cardio ? [d.sessions[today].cardio] : [];
    persist({ ...d, sessions: { ...d.sessions, [today]: { ...d.sessions[today], cardio: [...existing, cardioEntry] } } });
  }
  function handleRemoveCardio(index) {
    let d = ensureSession();
    const existing = Array.isArray(d.sessions[today]?.cardio) ? d.sessions[today].cardio : [];
    persist({ ...d, sessions: { ...d.sessions, [today]: { ...d.sessions[today], cardio: existing.filter((_, i) => i !== index) } } });
  }
  function handleSaveBodyweight(weight) {
    const existing = (data.bodyweights || []).filter(b => b.date !== today);
    persist({ ...data, bodyweights: [...existing, { date: today, weight }] });
  }
  function handleSaveProgram(customProgram) {
    persist({ ...data, customProgram });
  }

  const rd = { compound: data.settings?.compoundRest || 180, isolation: data.settings?.isolationRest || 90 };
  const timerActive = timerEndTime && timerEndTime > Date.now() - 3000;

  const navItems = [
    { id: "workout", label: "Workout", color: "#4f8fff", icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <line x1="6" y1="12" x2="18" y2="12" stroke={active ? "#4f8fff" : "#4a5978"} strokeWidth="2"/>
        <line x1="6" y1="8" x2="6" y2="16" stroke={active ? "#93c5fd" : "#374151"} strokeWidth="3"/>
        <line x1="18" y1="8" x2="18" y2="16" stroke={active ? "#93c5fd" : "#374151"} strokeWidth="3"/>
        <line x1="3" y1="10" x2="3" y2="14" stroke={active ? "#4f8fff" : "#4a5978"} strokeWidth="2.5"/>
        <line x1="21" y1="10" x2="21" y2="14" stroke={active ? "#4f8fff" : "#4a5978"} strokeWidth="2.5"/>
      </svg>
    )},
    { id: "history", label: "History", color: "#34d399", icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" fill={active ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.03)"} stroke={active ? "#34d399" : "#4a5978"} strokeWidth="1.8"/>
        <line x1="12" y1="7" x2="12" y2="12" stroke={active ? "#34d399" : "#4a5978"} strokeWidth="2"/>
        <line x1="12" y1="12" x2="15" y2="15" stroke={active ? "#6ee7b7" : "#374151"} strokeWidth="2"/>
        <circle cx="12" cy="12" r="1.2" fill={active ? "#34d399" : "#4a5978"}/>
      </svg>
    )},
    { id: "stats", label: "Stats", color: "#a78bfa", icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="4" y="14" width="3" height="7" rx="1" fill={active ? "#c4b5fd" : "#374151"}/>
        <rect x="8.5" y="9" width="3" height="12" rx="1" fill={active ? "#a78bfa" : "#4a5978"}/>
        <rect x="13" y="11.5" width="3" height="9.5" rx="1" fill={active ? "#c4b5fd" : "#374151"}/>
        <rect x="17.5" y="5" width="3" height="16" rx="1" fill={active ? "#a78bfa" : "#4a5978"}/>
      </svg>
    )},
    { id: "tools", label: "Tools", color: "#fbbf24", icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
          fill={active ? "rgba(251,191,36,0.12)" : "none"}
          stroke={active ? "#fbbf24" : "#4a5978"} strokeWidth="1.8"/>
      </svg>
    )},
    { id: "settings", label: "Settings", color: "#94a3b8", icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" fill={active ? "#94a3b8" : "#374151"} stroke={active ? "#cbd5e1" : "#4a5978"} strokeWidth="1.2"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
          stroke={active ? "#cbd5e1" : "#4a5978"} strokeWidth="1.6"/>
      </svg>
    )},
  ];

  return (
    <div style={{ height: "100dvh", maxWidth: "100vw", display: "flex", flexDirection: "column", background: "var(--bg-0)", overflow: "hidden" }}>
      <style>{CSS}</style>
      <div style={{ flexShrink: 0, height: "env(safe-area-inset-top)", background: "var(--bg-0)" }} />

      {tab === "workout" && !loggingEx && (
        <div style={{ flexShrink: 0, padding: "10px 16px 4px", display: "flex", justifyContent: "center", gap: 3 }}>
          {DEFAULT_PROGRAM.map((_, i) => {
            const d = getDayMeta(i, data.dayStructure);
            const isT = i === todayDow();
            const active = i === activeDow;
            const training = getDayExercises(i, data.customProgram, data.dayStructure).length > 0;
            return (
              <button key={i} onClick={() => setActiveDow(i)} style={{
                width: 44, height: 48, borderRadius: 10, border: "none", cursor: "pointer", fontFamily: "inherit",
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
                background: active ? (d.color ? `${d.color}15` : "rgba(79,143,255,0.1)") : "transparent",
                transition: "all 0.15s",
              }}>
                <span style={{ fontSize: 11, fontWeight: active ? 800 : 600, color: active ? (d.color || "var(--accent)") : "var(--text-3)" }}>{d.short}</span>
                {training ? (
                  <div style={{ width: 5, height: 5, borderRadius: 3, background: isT ? (active ? (d.color || "#fff") : "var(--accent)") : "rgba(255,255,255,0.08)" }} />
                ) : (
                  <div style={{ width: 14, height: 1, borderRadius: 1, background: active ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)" }} />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ flex: 1, overflow: "auto", overflowX: "hidden", overflowAnchor: "none", WebkitOverflowScrolling: "touch" }}>
        {tab === "workout" && <WorkoutView data={data} activeDow={activeDow} onStartExercise={setLoggingEx} onSkip={handleSkip} onSaveNotes={handleSaveNotes} onSaveCardio={handleSaveCardio} onRemoveCardio={handleRemoveCardio} onSwap={handleSwap} onRestoreSwap={handleRestoreSwap} />}
        {tab === "history" && <HistoryView data={data} />}
        {tab === "stats" && <StatsView data={data} onSaveBodyweight={handleSaveBodyweight} />}
        {tab === "tools" && <ToolsView />}
        {tab === "settings" && <SettingsView data={data} settings={data.settings || {}} onSave={st => persist({ ...data, settings: st })} onReset={() => persist({ ...defaultData })} onOpenProgramEditor={() => setShowProgramEditor(true)} onChangeSplit={() => setShowOnboarding(true)} onImport={imported => persist({ ...defaultData, ...imported })} appVersion={APP_VERSION} />}
      </div>

      {timerActive && !loggingEx && <RestTimer endTime={timerEndTime} totalSeconds={timerTotalSecs} onDismiss={handleDismissTimer} />}

      {!loggingEx && (
        <div style={{
          flexShrink: 0, display: "flex", background: "var(--bg-1)",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          paddingBottom: "max(16px, env(safe-area-inset-bottom))",
        }}>
          {navItems.map(n => {
            const active = tab === n.id;
            return (
              <button key={n.id} onClick={() => { setTab(n.id); if (n.id === "workout") setActiveDow(todayDow()); }}
                style={{
                  flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                  padding: "10px 0 4px", border: "none", cursor: "pointer", background: "transparent",
                  fontFamily: "inherit",
                }}>
                <div style={{ transform: active ? "scale(1.15)" : "scale(1)", transition: "transform 0.15s" }}>
                  {n.icon(active)}
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                  color: active ? n.color : "var(--text-3)",
                  transition: "color 0.15s",
                }}>{n.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {loggingEx && (
        <LoggingScreen exercise={loggingEx} sessions={data.sessions} dayIndex={activeDow}
          dayColor={getDayMeta(activeDow, data.dayStructure)?.color}
          startingWeights={data.startingWeights}
          onSave={handleSaveSets} onClose={() => setLoggingEx(null)} restDefaults={rd}
          timerEndTime={timerEndTime} timerTotalSecs={timerTotalSecs}
          onStartTimer={handleStartTimer} onDismissTimer={handleDismissTimer} />
      )}

      {showProgramEditor && (
        <ProgramEditorScreen data={data} onSave={handleSaveProgram} onClose={() => setShowProgramEditor(false)} />
      )}
    </div>
  );
}